import { z } from 'zod';

export const accountStatus = z.enum([
  'open',
  'closed',
  'suspended',
  'restricted',
  'not_activated',
  'unknown',
]);
export const spendPolicy = z.enum(['eligible', 'payment_only', 'maintenance_only', 'blocked']);
export const portfolioDisposition = z.enum([
  'keep',
  'likely_keep_after_payoff',
  'review_for_closure',
  'close_when_safe',
  'undecided',
]);
export const cardNetwork = z.enum([
  'visa',
  'mastercard',
  'amex',
  'discover',
  'closed_loop',
  'unknown',
]);
export const ownerType = z.enum(['personal', 'business']);
export const suffixKind = z.enum(['last_four', 'last_five', 'issuer_reference', 'unknown']);
export const graceStatus = z.enum([
  'active',
  'inactive',
  'likely_active',
  'likely_inactive',
  'restoration_pending',
  'unknown',
  'not_applicable',
]);
export const graceRestoreRule = z.enum([
  'immediate_on_full_payoff',
  'next_billing_cycle_after_full_payoff',
  'after_one_full_statement_cycle',
  'after_two_full_statement_cycles',
  'issuer_confirmation_required',
  'unknown',
]);
export const observationSource = z.enum([
  'issuer_dashboard',
  'statement',
  'issuer_phone',
  'user_transcription',
  'user_spreadsheet',
]);
export const autopayType = z.enum([
  'statement_balance',
  'minimum',
  'fixed',
  'current_balance',
  'none',
  'unknown',
]);
export const confidenceTier = z.enum([
  'account_agreement',
  'current_statement',
  'logged_in_account',
  'network_guide',
  'issuer_public_page',
  'user_reported',
  'third_party',
  'inferred',
  'unverified',
]);
export const evidenceScope = z.enum(['account_specific', 'public_product', 'network_guide']);
export const sourceType = z.enum([
  'cardmember_agreement',
  'rewards_program_supplement',
  'pricing_summary',
  'statement',
  'logged_in_screenshot',
  'payment_history_screen',
  'welcome_packet',
  'change_in_terms',
  'benefits_guide',
  'issuer_product_page',
  'customer_service_confirmation',
  'third_party_article',
  'user_confirmation',
]);
export const verificationStatus = z.enum([
  'confirmed',
  'user_reported',
  'inferred',
  'unverified',
  'outdated',
  'conflicting',
]);
export const rewardCurrency = z.enum(['cash', 'points', 'miles', 'store_credit', 'unknown']);
export const merchantScope = z.enum([
  'any',
  'named_merchant',
  'merchant_list',
  'portal_only',
  'excluded_list',
]);
export const txnChannel = z.enum([
  'any',
  'online',
  'in_store',
  'mobile_wallet',
  'phone',
  'unknown',
]);
export const geoScope = z.enum(['any', 'domestic', 'international']);
export const capPeriod = z.enum([
  'monthly',
  'quarterly',
  'annual',
  'per_transaction',
  'lifetime',
  'none',
]);
export const stackingRule = z.enum(['stacks_with_offers', 'exclusive', 'unknown']);
export const spendCategory = z.enum([
  'gas',
  'groceries',
  'grocery_superstore',
  'wholesale_club',
  'restaurants',
  'fast_food',
  'drugstores',
  'amazon',
  'online_retail',
  'department_store',
  'electronics',
  'home_improvement',
  'travel_portal',
  'airlines',
  'hotels',
  'rental_cars',
  'transit',
  'rideshare',
  'utilities',
  'cellphone',
  'internet',
  'streaming',
  'software_subscription',
  'business_software',
  'insurance',
  'medical',
  'education',
  'entertainment',
  'charity',
  'government',
  'wallet_or_p2p',
  'other',
  'unknown',
]);
export const benefitType = z.enum([
  'purchase_protection',
  'extended_warranty',
  'return_protection',
  'price_protection',
  'cellphone_protection',
  'trip_cancellation',
  'trip_interruption',
  'trip_delay',
  'trip_assistance',
  'rental_car_cdw',
  'lost_luggage',
  'baggage_delay',
  'travel_accident',
  'travel_emergency_assistance',
  'auto_deductible_reimbursement',
  'roadside_assistance',
  'concierge',
  'virtual_card_numbers',
  'fraud_liability',
  'other',
]);
export const coveragePosition = z.enum(['primary', 'secondary', 'n/a', 'unknown']);
export const offerType = z.enum([
  'percent_back',
  'statement_credit',
  'bonus_points',
  'fixed_discount',
]);
export const txnType = z.enum([
  'purchase',
  'refund',
  'statement_credit',
  'fee',
  'interest',
  'cash_advance',
  'balance_transfer',
  'adjustment',
  'reward_redemption',
]);
export const txnStatus = z.enum([
  'planned',
  'pending',
  'posted',
  'cleared',
  'refunded',
  'partially_refunded',
  'disputed',
  'reversed',
  'canceled',
]);
export const txnPaymentStatus = z.enum([
  'unassigned',
  'not_yet_statemented',
  'included_in_open_statement',
  'statemented',
  'partially_paid',
  'paid',
  'refunded',
  'disputed',
  'excluded_from_payment',
]);
export const cycleAssignment = z.enum(['confirmed', 'estimated', 'unassigned']);
export const cycleStatus = z.enum([
  'projected',
  'open',
  'closed',
  'statement_issued',
  'payment_due',
  'partially_paid',
  'paid',
  'past_due',
  'reconciled',
]);
export const paymentMethod = z.enum(['autopay', 'manual_push', 'issuer_pull', 'other']);
export const paymentStatus = z.enum([
  'planned',
  'scheduled',
  'processing',
  'posted',
  'returned',
  'canceled',
]);
export const allocationBasis = z.enum([
  'user_specified',
  'statement_first',
  'proportional',
  'unassigned',
]);
export const reservationStatus = z.enum(['reserved', 'released', 'converted_to_payment']);
export const priorityMode = z.enum([
  'maximize_rewards',
  'maximize_protection',
  'travel_coverage',
  'minimize_utilization',
  'lowest_risk',
  'large_purchase',
  'international',
  'financing_needed',
  'business_expense',
  'recurring_subscription',
  'avoid_annual_fee',
]);
export const categorySource = z.enum(['user', 'merchant_default', 'observed', 'unknown']);
export const feeKind = z.enum([
  'annual',
  'monthly_maintenance',
  'program_participation',
  'credit_protection',
  'over_limit_coverage',
  'authorized_user',
  'expedited_payment',
  'paper_statement',
  'other',
]);
export const feeFrequency = z.enum(['one_time', 'monthly', 'annual', 'per_occurrence', 'unknown']);
export const limitBasis = z.enum(['per_person', 'per_account', 'per_claim', 'per_year', 'unknown']);
